import Navbar from "../components/Navbar";
import PageHero from "../components/PageHero";
import Stats from "../components/Stats";
import About from "../components/About";
import PricingTiers from "../components/PricingTiers";
import Process from "../components/Process";
import Gallery from "../components/Gallery";
import CuratedReviews from "../components/CuratedReviews";
import FAQ from "../components/FAQ";
import Contact from "../components/Contact";
import Footer from "../components/Footer";
import {
  SEO,
  localBusinessSchema,
  serviceSchema,
  faqSchema,
  breadcrumbSchema,
} from "../components/SEO";
import { BRAND } from "../mock";

const FAQS = [
  {
    q: "Do you offer ceramic coating for Toronto clients?",
    a: "Yes. we serve Toronto, Etobicoke, North York, Scarborough, East York and downtown from our Mississauga studio. Free pickup and delivery is available within 25 km on any coating package over $1,000.",
  },
  {
    q: "How much does ceramic coating cost in Toronto?",
    a: "Our flagship 2-layer ceramic coating with a 1-stage paint correction included is currently $550. a limited content-based offer for Toronto vehicles we feature on our channels (regularly $1,500). Final pricing depends on vehicle size and paint condition. We provide a written, no-obligation quote within 24 hours.",
  },
  {
    q: "What is the best ceramic coating shop for Toronto drivers?",
    a: "Toronto enthusiasts make the short drive to Jester Werks because we're owner-operated, fully transparent on process, and back every coating in writing. With 250+ verified five-star Google reviews and 1,500+ cars coated, we're the most-reviewed and most transparent ceramic coating studio serving Toronto.",
  },
  {
    q: "How long does ceramic coating last in Toronto winters?",
    a: "Toronto winters are brutal on paint. salt, slush and freeze-thaw cycles strip unprotected clear coat. Our 2-layer Gtechniq ceramic coating creates a glass-deep barrier that shrugs off road salt and de-icers and keeps your paint looking glossy for the full 5-year warranty term.",
  },
  {
    q: "Can I drive my car back to Toronto right after coating?",
    a: "Most coatings need a controlled 12 to 24-hour cure before water exposure. We typically deliver vehicles to Toronto clients the day after the coating cures, so you don't have to risk early-cure rain or wait around in the studio.",
  },
  {
    q: "Which Toronto neighbourhoods do you serve?",
    a: "We serve all of Toronto. including Etobicoke, North York, Scarborough, East York, downtown core, Liberty Village, Leaside, The Beaches, Forest Hill, Rosedale, High Park, Bloor West, Yorkville, King West and the waterfront. Free pickup and delivery is available across the Toronto area on packages over $1,000.",
  },
  {
    q: "What does the $550 ceramic coating offer include?",
    a: "Our $550 Toronto offer includes: full decontamination prep, a 1-stage paint correction, the 2-layer Crystal Serum Light + EXO v5 ceramic system, a free window coating ($150 value), a written 5-year warranty and a documented before/after report. It is a limited content-based offer for select vehicles we feature.",
  },
];

export default function CeramicToronto() {
  const url = `${BRAND.url}/ceramic-coating-toronto`;
  return (
    <div className="grain bg-jester-deep text-white">
      <SEO
        title="Ceramic Coating Toronto | Free Pickup & Delivery. From $550 | Jester Werks"
        description="Ceramic coating Toronto from $550. Toronto's most-trusted ceramic coating studio (just across the border in Mississauga). 250+ verified Google reviews, 5-year warranty, free window coating, free pickup & delivery within 25 km. Etobicoke, North York, Scarborough, downtown. Call (647) 646-7223."
        canonical={url}
        schemas={[
          localBusinessSchema(),
          serviceSchema({
            name: "Ceramic Coating Toronto",
            description:
              "Premium 2-layer Gtechniq ceramic coating service for Toronto drivers. Hand-applied Gtechniq Crystal Serum Light + EXO v5 with 1-stage paint correction, 5-year warranty, free window coating. Free pickup and delivery within 25 km of Toronto.",
            areaServed: [
              "Toronto", "Etobicoke", "North York", "Scarborough", "East York",
              "Downtown Toronto", "The Beaches", "Liberty Village", "Vaughan", "Markham",
            ],
            offers: "550",
          }),
          faqSchema(FAQS),
          breadcrumbSchema([
            { name: "Home", url: `${BRAND.url}/` },
            { name: "Ceramic Coating Toronto", url },
          ]),
        ]}
      />
      <Navbar />
      <PageHero
        kicker="Toronto · Free Pickup From the GTA"
        h1="CERAMIC COATING"
        h1Accent="TORONTO."
        marker="we'll come to you."
        description="Toronto's most-trusted Gtechniq ceramic coating studio is just across the border in Mississauga. with free pickup & delivery within 25 km. 2-layer Gtechniq ceramic with paint correction included, now from $550 (regularly $1,500)."
        city="Toronto"
        bgImage="https://images.unsplash.com/photo-1580679568899-be51739ba2df"
        secondaryCta={{ label: "View Packages", href: "#pricing" }}
      />

      <section className="relative py-24 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-acid mb-5 font-label">
           Toronto's Detail Studio
          </div>
          <h2 className="font-display text-4xl md:text-5xl leading-[1] mb-8">
            <span className="text-jester">Ceramic coating</span> built for{" "}
            Toronto winters.
          </h2>
          <div className="text-white/75 text-lg leading-relaxed space-y-5">
            <p>
              When Toronto enthusiasts want{" "}
              <strong className="text-white">
                ceramic coating they can actually trust
              </strong>
              , they make the short drive to Jester Werks. Why? Because we are
              owner-operated, hand-finish every car, document the entire
              process with before/after photos, and back every coating with a
              written 5-year warranty. We have{" "}
              <strong className="text-acid">
                250+ verified five-star Google reviews
              </strong>{" "}
              and 1,500+ cars coated since 2023. making us the most-reviewed
              and most transparent ceramic coating studio serving the Toronto
              market.
            </p>
            <p>
              Our flagship Toronto ceramic coating package pairs Gtechniq's
              flagship hard base coat (<em>Gtechniq Crystal Serum Light</em>)
              with a high-gloss hydrophobic top coat (<em>Gtechniq EXO v5</em>)
             a true two-layer Gtechniq system, hand-applied panel by panel
              in a climate-controlled bay. The Gtechniq coating bonds
              chemically to your factory clear coat, creating a glass-deep
              shield specifically built for Canadian climates: salt, slush,
              road grime, freeze-thaw cycles. The Gtechniq molecular bond
              shrugs off everything Toronto winters throw at your paint, while
              the hydrophobic finish makes spring washes a 10-minute job.
            </p>
            <p>
              Right now we're running a limited{" "}
              <strong className="text-acid">$550 content-based ceramic
              coating offer</strong> (regularly $1,500) for select Toronto
              vehicles we feature on our channels. The package includes a
              full decontamination prep, 1-stage paint correction, the
              complete 2-layer ceramic system, a{" "}
              <strong className="text-white">free window coating</strong>{" "}
              ($150 value), a written 5-year warranty and a documented
              before/after report.
            </p>
            <p>
              We serve all of Toronto.{" "}
              <strong className="text-white">
                Etobicoke, North York, Scarborough, East York, downtown core,
                Liberty Village, Leaside, The Beaches, Forest Hill, Rosedale,
                High Park, Bloor West, Yorkville, King West
              </strong>{" "}
              and the waterfront. plus the broader GTA including{" "}
              <a href="/ceramic-coating-mississauga" className="text-acid underline underline-offset-4 hover:text-white">
                Mississauga
              </a>
              ,{" "}
              <a href="/ceramic-coating-vaughan" className="text-acid underline underline-offset-4 hover:text-white">
                Vaughan
              </a>
              , Oakville, Brampton, Burlington, Markham and Richmond Hill.
              <strong className="text-white"> Free pickup &amp; delivery</strong>{" "}
              is available within 25 km on any package over $1,000.
            </p>
            <p>
              Whether you searched{" "}
              <em className="text-white/90">"ceramic coating Toronto"</em>,{" "}
              <em className="text-white/90">"best ceramic coating Toronto"</em>,{" "}
              <em className="text-white/90">"ceramic coating near me"</em> or{" "}
              <em className="text-white/90">"Gtechniq Toronto"</em>, you've
              landed on the highest-rated, most transparent ceramic coating
              studio serving the Toronto market. Call (647) 646-7223 or book
              online for a free written quote. delivered in under 24 hours.
            </p>
          </div>
        </div>
      </section>

      <Stats />
      <PricingTiers city="Toronto" />
      <About />
      <Process />
      <Gallery />
      <CuratedReviews />
      <FAQ items={FAQS} />
      <Contact />
      <Footer />
    </div>
  );
}
